import type { TPortalVenueType } from '@lucimark/shared/database/marketplace-tenant-schema'
import type {
  TPortalAspectLoopSettings,
  TPortalLoopSettingsByAspectRatio
} from '@lucimark/shared/helpers/portalLoopSettingsByAspectRatio'
import { MARKETPLACE_PLAYER_BOOKING_QR_CTA_MAX_LENGTH } from '@lucimark/shared/constants/marketplace'
import { AspectRatio } from '@lucimark/shared/database/custom-types/aspect-ratio'
import { PORTAL_VENUE_TYPES } from '@lucimark/shared/database/marketplace-tenant-schema'
import { APP_LOCALES } from '@lucimark/shared/helpers/appLocale'
import { playlistEmbedPlaybackSchema } from '@lucimark/shared/models/playlist-embed-playback'
import z from 'zod'

export type { TPortalAspectLoopSettings, TPortalLoopSettingsByAspectRatio }

const portalAspectLoopSettingsSchema = z.object({
  houseContentPlaylistUrn: z.string().nullable(),
  tailFillerAssetUrn: z.string().nullable()
}) satisfies z.ZodType<TPortalAspectLoopSettings>

export const portalLoopSettingsByAspectRatioSchema = z.object({
  [AspectRatio.Horizontal]: portalAspectLoopSettingsSchema,
  [AspectRatio.Vertical]: portalAspectLoopSettingsSchema,
  [AspectRatio.Square]: portalAspectLoopSettingsSchema
}) satisfies z.ZodType<TPortalLoopSettingsByAspectRatio>

export { PORTAL_VENUE_TYPES, type TPortalVenueType }

export const marketplaceScreenSummarySchema = z.object({
  tenantId: z.string(),
  playerId: z.string(),
  tenantHandle: z.string().nullable().optional(),
  tenantName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  venueName: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  venueType: z.string().nullable().optional(),
  orientation: z.string().nullable().optional(),
  screenWidthPx: z.number().int().nullable().optional(),
  screenHeightPx: z.number().int().nullable().optional(),
  slotPricePerDayMinor: z.number().int().nullable().optional(),
  loopCapacity: z.number().int().nullable().optional(),
  slotDurationSeconds: z.number().int().nullable().optional(),
  currency: z.string().nullable().optional(),
  photoCount: z.number().int().nonnegative(),
  primaryPhotoUrl: z.string().nullable().optional(),
  effectiveOperatingHoursPerDay: z.number().nullable().optional(),
  expectedPlaysPerSlotPerDay: z.number().int().nullable().optional(),
  costPerPlayMinor: z.number().int().nullable().optional(),
  lastReportedUtcOffsetMinutes: z.number().int().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  positionPrecision: z.enum(['precise', 'centroid']).nullable().optional()
})

export type TMarketplaceScreenSummary = z.infer<typeof marketplaceScreenSummarySchema>

export const marketplaceScreenPhotoSchema = z.object({
  id: z.string(),
  assetUrn: z.string(),
  sortOrder: z.number().int(),
  caption: z.string().nullable().optional(),
  url: z.string().nullable().optional()
})

export const marketplaceScreenDetailSchema = marketplaceScreenSummarySchema.extend({
  placementNotes: z.string().nullable().optional(),
  audienceDescription: z.string().nullable().optional(),
  contentRestrictions: z.string().nullable().optional(),
  termsUrl: z.string().nullable().optional(),
  photos: z.array(marketplaceScreenPhotoSchema)
})

export type TMarketplaceScreenDetail = z.infer<typeof marketplaceScreenDetailSchema>

export const marketplaceScreensQuerySchema = z.object({
  tenantId: z.string().optional(),
  city: z.string().optional(),
  venueType: z.string().optional(),
  /** Comma-separated venue type slugs (e.g. `gym,retail`). */
  venue: z.string().optional(),
  /** Comma-separated tenant handles (root map operator filter). */
  operator: z.string().optional(),
  bbox: z.string().regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?$/).optional(),
  cpsMin: z.coerce.number().int().min(0).optional(),
  cpsMax: z.coerce.number().int().min(0).optional(),
  orientation: z.enum(['portrait', 'landscape']).optional(),
  /** Inclusive campaign start (`YYYY-MM-DD`, UTC). Requires `to`. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Inclusive campaign end (`YYYY-MM-DD`, UTC). Requires `from`. */
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  cursor: z.string().optional()
}).refine(
  query => (query.from == null) === (query.to == null),
  { message: 'from and to must be provided together' }
)

export const marketplaceScreenMapSummarySchema = marketplaceScreenSummarySchema.extend({
  cpsFrom: z.number().int().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  available: z.boolean().optional()
})

export type TMarketplaceScreenMapSummary = z.infer<typeof marketplaceScreenMapSummarySchema>

export const marketplaceScreensMapResponseSchema = z.object({
  screens: z.array(marketplaceScreenMapSummarySchema),
  nextCursor: z.string().nullable().optional()
})

export const portalAdvertiserLocaleSchema = z.enum(APP_LOCALES)

export const portalTaxModelSchema = z.enum(['exclusive', 'inclusive', 'additive', 'compound'])
export type TPortalTaxModel = 'exclusive' | 'inclusive'

export const portalTaxItemSchema = z.object({
  label: z.string().trim().min(1).max(60),
  percent: z.number().min(0).max(100)
})
export type TPortalTaxItem = z.infer<typeof portalTaxItemSchema>

export const portalTaxConfigSchema = z.object({
  model: portalTaxModelSchema,
  items: z.array(portalTaxItemSchema).max(10)
})
export type TPortalTaxConfig = z.infer<typeof portalTaxConfigSchema>

export const portalBillingSettingsSchema = z.object({
  tenantId: z.string(),
  portalListedOnNetwork: z.boolean(),
  advertiserLocale: portalAdvertiserLocaleSchema,
  currency: z.string(),
  loopCapacity: z.number().int().positive(),
  slotDurationSeconds: z.number().int().positive(),
  slotPricePerDayMinor: z.number().int().nonnegative(),
  taxConfig: portalTaxConfigSchema,
  taxPercent: z.number(),
  termsUrl: z.string().nullable().optional(),
  reviewSlaHours: z.number().int().nullable().optional(),
  reviewNotificationEmail: z.string().nullable().optional(),
  houseContentPlaylistUrn: z.string().nullable().optional(),
  houseContentPlaylistEmbedPlayback: playlistEmbedPlaybackSchema.nullable().optional(),
  tailFillerAssetUrn: z.string().nullable().optional(),
  loopSettingsByAspectRatio: portalLoopSettingsByAspectRatioSchema.nullable().optional(),
  playerBookingQrCta: z.string().max(MARKETPLACE_PLAYER_BOOKING_QR_CTA_MAX_LENGTH).nullable().optional(),
  playerBookingQrEnabled: z.boolean().default(true),
  updatedAt: z.coerce.date()
})

export type TPortalBillingSettings = z.infer<typeof portalBillingSettingsSchema>

export const upsertPortalBillingSettingsBodySchema = z.object({
  portalListedOnNetwork: z.boolean().optional(),
  currency: z.string().length(3).optional(),
  loopCapacity: z.number().int().min(1).max(24).optional(),
  slotDurationSeconds: z.number().int().min(5).max(120).optional(),
  slotPricePerDayMinor: z.number().int().min(0).optional(),
  taxConfig: portalTaxConfigSchema.optional(),
  termsUrl: z.string().url().nullable().optional(),
  reviewSlaHours: z.number().int().min(1).nullable().optional(),
  reviewNotificationEmail: z.string().email().nullable().optional(),
  houseContentPlaylistUrn: z.string().nullable().optional(),
  houseContentPlaylistEmbedPlayback: playlistEmbedPlaybackSchema.nullable().optional(),
  tailFillerAssetUrn: z.string().nullable().optional(),
  loopSettingsByAspectRatio: portalLoopSettingsByAspectRatioSchema.nullable().optional(),
  playerBookingQrCta: z.string().trim().max(MARKETPLACE_PLAYER_BOOKING_QR_CTA_MAX_LENGTH).nullable().optional(),
  playerBookingQrEnabled: z.boolean().optional()
})

/** Listing map pin and copy only — venue type is derived from the screen's assigned venue. */
export const upsertPlayerPortalListingBodySchema = z.object({
  listingLatitude: z.number().min(-90).max(90).nullable().optional(),
  listingLongitude: z.number().min(-180).max(180).nullable().optional(),
  addressLine: z.string().max(300).optional(),
  audienceDescription: z.string().max(4000).optional(),
  contentRestrictions: z.string().max(2000).optional()
}).refine(
  (body) => {
    const hasLat = body.listingLatitude != null
    const hasLng = body.listingLongitude != null
    return hasLat === hasLng
  },
  { message: 'listingLatitude and listingLongitude must be set together' }
)

export const setPlayerPortalBookableBodySchema = z.object({
  portalBookable: z.boolean()
})

/** Dev/staging tenant wipe of Book ad orders tied to the operator tenant. */
export const marketplaceDevWipeAdOrdersResponseSchema = z.object({
  deletedOrderCount: z.number().int().nonnegative()
})

export type TMarketplaceDevWipeAdOrdersResponse = z.infer<typeof marketplaceDevWipeAdOrdersResponseSchema>

export const playerPortalListingSchema = z.object({
  playerId: z.string(),
  venueName: z.string().nullable().optional(),
  venueType: z.enum(PORTAL_VENUE_TYPES).nullable().optional(),
  venueTypeId: z.string().uuid().nullable().optional(),
  addressLine: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  placementNotes: z.string().nullable().optional(),
  audienceDescription: z.string().nullable().optional(),
  contentRestrictions: z.string().nullable().optional(),
  sharePreciseLocation: z.boolean().optional(),
  listingLatitude: z.number().nullable().optional(),
  listingLongitude: z.number().nullable().optional(),
  cityCentroidLat: z.number().nullable().optional(),
  cityCentroidLng: z.number().nullable().optional(),
  updatedAt: z.coerce.date()
})

export type TPlayerPortalListing = z.infer<typeof playerPortalListingSchema>

/** Operator with at least one live portal-bookable screen on the network catalog. */
export const marketplaceBookableOperatorSummarySchema = z.object({
  tenantId: z.string(),
  handle: z.string(),
  name: z.string(),
  screenCount: z.number().int().min(1),
  advertiserLocale: portalAdvertiserLocaleSchema
})

export const marketplaceBookableOperatorsResponseSchema = z.object({
  operators: z.array(marketplaceBookableOperatorSummarySchema)
})

export type TMarketplaceBookableOperatorSummary = z.infer<typeof marketplaceBookableOperatorSummarySchema>
